/**
 * useFlicker.js
 *
 * Detects when a numeric value changes and returns a short-lived direction
 * signal ('up' | 'down' | null) so cells can flash green/red.
 *
 * Usage:
 *   const flicker = useFlicker(ltp, 600);   // flicker for 600ms
 */
import { useEffect, useRef, useState } from 'react';

export default function useFlicker(value, durationMs = 600) {
    const prevRef = useRef(value);
    const timerRef = useRef(null);
    const [flicker, setFlicker] = useState(null); // 'up' | 'down' | null

    useEffect(() => {
        // Skip on first render (no previous value to compare against)
        if (prevRef.current === undefined || prevRef.current === null) {
            prevRef.current = value;
            return;
        }

        const prev = prevRef.current;
        prevRef.current = value;

        if (value === prev || value == null) return;

        // Clear any running timer so rapid ticks don't stack
        if (timerRef.current) clearTimeout(timerRef.current);

        setFlicker(value > prev ? 'up' : 'down');
        timerRef.current = setTimeout(() => setFlicker(null), durationMs);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [value, durationMs]);

    return flicker;
}
